import { useEffect, useState } from 'react';
import { useSanitizedAudioSrc } from 'hooks/useSanitizedUnitAudio';
import { useUnit } from 'state/unit';
import { isSegmentable } from 'utils/audio';
import { usePlayback } from './Player';
import UnloadingAudio from './UnloadingAudio';

export interface PublisherPlayerProps {}

/*
 * Publisher attribution player that stays in-sync with the main audio player.
 */
const PublisherPlayer: React.FC<PublisherPlayerProps> = () => {
  const unit = useUnit();
  const sanitizedSrc = useSanitizedAudioSrc(unit?.episode.originalAudioUrl);
  const { player } = usePlayback();
  const [publisher, setPublisher] = useState<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = publisher;

    if (player && audio && isSegmentable(player)) {
      const handlePlay = () => {
        audio.play().catch(() => {
          /* ignore */
        });
      };

      const handlePause = () => {
        audio.pause();
      };

      const handleSeek = () => {
        audio.currentTime = player.rawCurrentTime();
      };

      player.on('play', handlePlay);
      player.on('pause', handlePause);
      player.on('seeked', handleSeek);

      return () => {
        if (!audio.paused) {
          audio.pause();
        }

        player.off('play', handlePlay);
        player.off('pause', handlePause);
        player.off('seeked', handleSeek);
      };
    }
  }, [player, publisher]);

  return !sanitizedSrc ? null : (
    <UnloadingAudio
      data-testid="publisher-player"
      loop
      muted
      preload="none"
      ref={setPublisher}
      src={sanitizedSrc}
    />
  );
};

export default PublisherPlayer;
